import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError, BaseResponse } from '@utils/types/shared/base';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ADMIN_SETTINGS_QUERY_KEY } from './use-get-admin-settings';

export type AdminSettingsUpdate = {
  retention_days_default?: string;
  max_upload_size_mb?: string;
  default_language?: string;
  maintenance_mode?: string;
};

export type AdminSettingsResponse = BaseResponse<AdminSettingsUpdate>;

/**
 * Hook to update on-prem admin settings
 *
 * PUT /api/admin/on-prem/settings
 *
 * Updates system-wide settings (retention days, max upload size, default language, maintenance mode)
 */
export const useUpdateAdminSettings = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const updateSettings = async (payload: AdminSettingsUpdate) => {
    const url = `api/admin/on-prem/settings`;
    const response = await Axios.put<AdminSettingsResponse>(url, payload);
    return response.data;
  };

  return useMutation<
    AdminSettingsResponse,
    AxiosError<BaseError>,
    AdminSettingsUpdate
  >(updateSettings, {
    onSuccess: () => {
      toast.success(t('settings-saved-successfully'));
      queryClient.invalidateQueries([ADMIN_SETTINGS_QUERY_KEY]);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-save-settings');
      toast.error(errorMessage);
    }
  });
};
